import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Typography,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress
} from '@mui/material';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import { FilterItem, SupervisorCustomerReportItem } from '../../../../../types/sales';
import { useAuth } from 'react-oidc-context';

// propsインターフェースを拡張
interface BatchPrintDialogProps {
  open: boolean;
  onClose: () => void;
  supervisors: FilterItem[];
  selectedSupervisors: string[];
  onSelectAllSupervisors: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSupervisorCheckboxChange: (supervisorName: string) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  startMonth: string;
  endMonth: string;
  isPrinting: boolean;
  setIsPrinting: (value: boolean) => void;
  // 追加: レポートキャプチャ用の関数とデータ
  onCaptureSupervisorReport?: (supervisorName: string, callback: (html: string) => void) => void;
  reportData?: SupervisorCustomerReportItem[];
}

// ウィンドウ型拡張インターフェース（updateProgress関数を追加）
interface PrintWindow extends Window {
  updateProgress?: (current: number) => void;
}

export const BatchPrintDialog: React.FC<BatchPrintDialogProps> = ({
  open,
  onClose,
  supervisors,
  selectedSupervisors,
  onSelectAllSupervisors,
  onSupervisorCheckboxChange,
  startMonth,
  endMonth,
  isPrinting,
  setIsPrinting,
  // 追加されたプロパティ
  onCaptureSupervisorReport,
  reportData
}) => {
  const auth = useAuth();

  const handleBatchPrint = async () => {
    if (selectedSupervisors.length === 0) return;

    setIsPrinting(true);

    try {
      // 印刷用ウィンドウを開く（型を指定）
      const printWindow = window.open('', '_blank') as PrintWindow | null;
      if (!printWindow) {
        throw new Error('印刷用ウィンドウを開けませんでした。ポップアップがブロックされている可能性があります。');
      }

      // 現在の年月を取得
      const currentYearMonth = `${startMonth.split('-')[0]}年${parseInt(startMonth.split('-')[1])}月`;

      // HTMLヘッダー部分を書き込む - スタイルは既存のものを使用
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>担当者別売上レポート (一括印刷)</title>
          <style>
            body {
              margin: 0;
              padding: 0;
              font-family: Arial, sans-serif;
              background-color: #f5f5f5;
            }
            
            .page {
              width: 210mm;  /* A4用紙の幅 */
              height: 297mm;  /* A4用紙の高さ */
              margin: 0 auto 20px;
              background-color: white;
              box-shadow: 0 0 10px rgba(0,0,0,0.1);
              padding: 15mm;
              box-sizing: border-box;
              page-break-after: always;
              position: relative;
            }
            
            /* 最後のページにはpage-breakを適用しない */
            .page:last-child {
              page-break-after: auto;
            }
            
            .loading-indicator {
              position: fixed;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              background-color: rgba(255, 255, 255, 0.9);
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 10px rgba(0,0,0,0.2);
              text-align: center;
              z-index: 9999;
            }
            
            .print-button {
              position: fixed;
              bottom: 20px;
              right: 20px;
              background-color: #4CAF50;
              color: white;
              padding: 10px 20px;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              font-size: 16px;
              z-index: 1000;
            }
            
            @media print {
              body {
                background-color: white;
              }
              
              .page {
                width: 100%;
                height: 100%;
                box-shadow: none;
                padding: 0;
                margin: 0;
              }
              
              .print-button, .loading-indicator {
                display: none !important;
              }
              
              @page {
                size: A4 portrait;
                margin: 10mm;
              }
            }
          </style>
        </head>
        <body>
          <div id="loadingIndicator" class="loading-indicator">
            レポートを生成中...<br />
            <span id="loadingStatus">0/${selectedSupervisors.length} 完了</span>
          </div>
          
          <button id="printButton" class="print-button" style="display: none;">印刷する</button>
          
          <div id="pagesContainer">
            <!-- 各担当者のレポートがここに追加される -->
          </div>
          
          <script>
            // ページ生成進捗状況の追跡
            let pagesGenerated = 0;
            let totalPages = ${selectedSupervisors.length};
            const loadingIndicator = document.getElementById('loadingIndicator');
            const loadingStatus = document.getElementById('loadingStatus');
            const printButton = document.getElementById('printButton');
            
            // 進捗状況更新関数
            function updateProgress(current) {
              pagesGenerated = current;
              
              if (loadingStatus) {
                loadingStatus.textContent = \`\${pagesGenerated}/\${totalPages} 完了\`;
              }
              
              if (pagesGenerated >= totalPages) {
                if (loadingIndicator) {
                  loadingIndicator.style.display = 'none';
                }
                
                if (printButton) {
                  printButton.style.display = 'block';
                }
              }
            }
            
            // グローバルに関数を公開
            window.updateProgress = updateProgress;
            
            // ウィンドウロード完了時の処理
            window.addEventListener('load', function() {
              // タイムアウト処理（10秒後）
              setTimeout(() => {
                if (pagesGenerated < totalPages) {
                  console.log('タイムアウト: 一部のページが生成されませんでした');
                  if (loadingIndicator) {
                    loadingIndicator.style.display = 'none';
                  }
                  
                  if (printButton) {
                    printButton.style.display = 'block';
                  }
                }
              }, 10000);
            });
            
            // 印刷ボタンのイベント
            if (printButton) {
              printButton.addEventListener('click', function() {
                this.style.display = 'none';
                
                // 印刷実行
                setTimeout(() => {
                  window.print();
                  
                  // 印刷後にウィンドウを閉じる（IE11対応）
                  if (typeof window.addEventListener === 'function') {
                    window.addEventListener('afterprint', function() {
                      setTimeout(() => window.close(), 500);
                    }, { once: true });
                  }
                  
                  // バックアップタイマー
                  setTimeout(() => window.close(), 10000);
                }, 500);
              });
            }
          </script>
        </body>
        </html>
      `);

      // ページコンテナを取得
      const pagesContainer = printWindow.document.getElementById('pagesContainer');

      // 進捗状況更新関数
      const updatePrintProgress = (current: number): void => {
        if (printWindow && !printWindow.closed) {
          try {
            // updateProgress関数が存在する場合のみ呼び出す
            if (printWindow.updateProgress) {
              printWindow.updateProgress(current);
            }
          } catch (e) {
            console.error('進捗更新エラー:', e);
          }
        }
      };

      // キャプチャ関数が提供されていない場合は従来の方法にフォールバック
      if (!onCaptureSupervisorReport) {
        printWindow.document.close();
        throw new Error('レポートキャプチャ関数が提供されていません。');
      }

      // 各担当者について処理
      let processedCount = 0;

      // IE11互換のforループを使用
      for (let i = 0; i < selectedSupervisors.length; i++) {
        const supervisorName = selectedSupervisors[i];

        try {
          console.log(`処理開始: ${supervisorName}`);

          // レポートHTMLをキャプチャ（Promise化）
          const reportHTML = await new Promise<string>((resolve) => {
            // 親コンポーネントに現在の担当者を選択するよう通知
            if (onCaptureSupervisorReport) {
              onCaptureSupervisorReport(supervisorName, (capturedHTML) => {
                resolve(capturedHTML);
              });
            } else {
              resolve(''); // キャプチャ関数がない場合は空文字列を返す
            }
          });

          // ページ要素を作成
          const pageDiv = printWindow.document.createElement('div');
          pageDiv.className = 'page';
          pageDiv.innerHTML = reportHTML;

          // ページをDOMに追加
          if (pagesContainer) {
            pagesContainer.appendChild(pageDiv);
          }

          // 進捗更新
          processedCount++;
          updatePrintProgress(processedCount);

          // 次の担当者の処理前に少し待機（IE11対応）
          if (i < selectedSupervisors.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }

        } catch (error) {
          console.error(`${supervisorName}のレポート生成中にエラーが発生しました:`, error);
        }
      }

      // すべてのページが追加されなかった場合のフォールバック
      if (processedCount === 0) {
        throw new Error('レポートページの生成に失敗しました。');
      }

      // ドキュメントを閉じる
      printWindow.document.close();

    } catch (error: unknown) {
      console.error('一括印刷処理でエラーが発生しました:', error);
      // エラーオブジェクトのメッセージプロパティを安全に取得
      const errorMessage = error instanceof Error ? error.message : '不明なエラー';
      alert(`印刷処理中にエラーが発生しました: ${errorMessage}`);
    } finally {
      setIsPrinting(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="batch-print-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="batch-print-dialog-title">
        担当者一括印刷
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          印刷する担当者を選択してください。複数選択可能です。
        </DialogContentText>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedSupervisors.length === supervisors.length}
                indeterminate={selectedSupervisors.length > 0 && selectedSupervisors.length < supervisors.length}
                onChange={onSelectAllSupervisors}
              />
            }
            label="すべて選択/解除"
          />
        </Box>

        <Box sx={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e0e0e0', p: 1, borderRadius: 1 }}>
          <FormGroup>
            {supervisors.map((supervisor) => (
              <FormControlLabel
                key={supervisor.name}
                control={
                  <Checkbox
                    checked={selectedSupervisors.includes(supervisor.name)}
                    onChange={onSupervisorCheckboxChange(supervisor.name)}
                  />
                }
                label={supervisor.name}
              />
            ))}
          </FormGroup>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          選択された担当者: {selectedSupervisors.length} / {supervisors.length}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          キャンセル
        </Button>
        <Button
          onClick={handleBatchPrint}
          color="primary"
          variant="contained"
          disabled={selectedSupervisors.length === 0 || isPrinting}
          startIcon={isPrinting ? <CircularProgress size={16} /> : <LocalPrintshopIcon />}
        >
          {isPrinting ? '処理中...' : '印刷する'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};